import ApplicationModel, { attr } from '../ApplicationModel';
import EstimateTypeFilter, {
  type EstimateTypeFilterValue,
} from '../filters/EstimateTypeFilter';
import type ItemFilter from '../filters/ItemFilter';
import IterationFilter from '../filters/IterationFilter';
import UserFilter from '../filters/UserFilter';
import { nameMap } from '../lib/nameMap';
import { model } from '../modelBuilder';
import { type RecordId } from 'javascripts/shared-types';
import type Iteration from './Iteration';
import User from './User';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { HasIterations } from './concerns/HasIterations';
import { type BookmarksIterationBurndownAttributes } from './generated/BookmarksIterationBurndownAttributes';
import { type UnitType } from '../lib/Estimate';

interface Filters {
  iterations?: RecordId[];
  users?: RecordId[];
  estimation_type?: EstimateTypeFilterValue[];
}

interface Local {
  filterObjects: Record<string, ItemFilter<any>>;
  users: User[];
}

interface BookmarksIterationBurndown
  extends BookmarksIterationBurndownAttributes,
    HasBookmarkFilters<{
      iterations: IterationFilter;
      users: UserFilter;
      estimation_type: EstimateTypeFilter;
    }>,
    HasIterations {
  filters: Filters;
  local: Partial<Local>;
}
@model
@HasBookmarkFilters
@HasIterations
class BookmarksIterationBurndown extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksIterationBurndown' = 'BookmarksIterationBurndown';
  get typename(): 'BookmarksIterationBurndown' {
    return 'BookmarksIterationBurndown';
  }

  static internalTypename: 'Bookmarks::IterationBurndown' =
    'Bookmarks::IterationBurndown';
  get internalTypename(): 'Bookmarks::IterationBurndown' {
    return 'Bookmarks::IterationBurndown';
  }

  static bookmarkName = 'Bookmarks::IterationBurndown';

  static inputType = 'IterationBurndownAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
  };
  // End auto-generated

  initialize() {
    this.local = { filterObjects: {} };
    this.filters = this.filters || {};
  }

  /**
   * Initialize the filters
   */
  async createFilters() {
    const [iterations, users] = await Promise.all([
      this.availableIterations(),
      this.availableUsers(),
    ]);

    let iteration: Iteration | undefined;
    if (iterations && this.filters.iterations) {
      const iterationId = this.filters.iterations[0];
      iteration = iterations.find(i => i.id === iterationId);
    }

    if (!iteration && iterations && iterations.length > 0) {
      iteration = iterations[0];
    }

    let defaultEstimationType: EstimateTypeFilterValue = 'count';

    if (iteration?.defaultEstimationType === 'original') {
      defaultEstimationType = 'originalEstimate';
    }

    const estimateTypeFilter = new EstimateTypeFilter({
      initialItems: this.filters.estimation_type || [defaultEstimationType],
      capacityUnits: iteration?.originalEstimate?.units,
      showRemainingEstimate: window.currentAccount?.teamworkOrDevelopAdvanced,
    });

    const iterationFilter = new IterationFilter(
      this.filters.iterations || [],
      (iterations ?? []).map(nameMap)
    );
    const userFilter = new UserFilter(
      this.filters.users || [],
      users.map(nameMap)
    );

    return {
      iterations: iterationFilter,
      users: userFilter,
      estimation_type: estimateTypeFilter,
    };
  }

  canFilter(scope: string) {
    return true;
  }

  /**
   * Load all the users available to this team
   */
  async availableUsers() {
    if (!this.local.users)
      this.local.users = await User.select('id', 'name', 'initials', 'email')
        .select({ avatarUrl: { size: aha.enums.AvatarSizeEnum.SIZE_40 } })
        .where({ projectId: this.projectId, includeAll: true })
        .per(100)
        .all();
    return this.local.users;
  }

  /**
   * Load the selected iteration using the available iterations
   */
  async iteration(): Promise<Iteration | undefined> {
    return this.getIteration({
      iterationId: this.getFilter('iterations')?.single(),
    });
  }

  /**
   * Load the selected user
   */
  async user() {
    const userId = this.getFilter('users')?.single();
    if (!userId) return;

    const users = await this.availableUsers();
    return users.find(u => u.id === userId);
  }

  /**
   * Load the selected unit
   */
  async units(): Promise<UnitType> {
    const units = this.getFilter('estimation_type')?.single();

    if (units === 'count') {
      return 'COUNT';
    } else {
      const iteration = await this.iteration();
      return iteration?.originalEstimate?.units ?? 'POINTS';
    }
  }

  /**
   * Fetch every single record event for the selected iteration
   *
   * @param filters Any filters to apply to the raw data fetch.
   */
  async recordEvents(filters: Record<string, any> = {}) {
    const iteration = await this.iteration();
    return iteration?.eventsRaw(filters);
  }
}

export default BookmarksIterationBurndown;
